import {
  Firestore,
  deleteField,
  getDoc,
  writeBatch
} from 'firebase/firestore';

import { addEntitySaveToBatch, addSummaryPathUpdateToBatch } from './batch';

import type {
  EntityEventChange,
  EventEntityChange,
  FieldChange,
  Novel,
  PrimaryEntityType
} from '@/lib/types';

import { StorageResolver } from '@/firebase/firestore/storage-resolver';
import { generateReciprocalDeltas } from '@/lib/utils/generate-reciprocal-deltas';

export async function saveEventImplication(
  firestore: Firestore,
  ownerId: string,
  novel: Novel,
  eventId: string,
  eventName: string,
  eventOrder: number,
  entityType: PrimaryEntityType,
  entityId: string,
  entityName: string,
  changes: FieldChange[],
  description: string,
  isEdit: boolean,
  isReciprocal = false
): Promise<void> {
  const resolver = new StorageResolver(firestore);
  const batch = writeBatch(firestore);

  const eventRef = resolver.resolveItem(ownerId, novel.id, 'entities', 'events', eventId, novel.storageLayout).ref;
  const entityRef = resolver.resolveItem(ownerId, novel.id, 'entities', entityType, entityId, novel.storageLayout).ref;

  const [eventSnap, entitySnap] = await Promise.all([
    getDoc(eventRef),
    getDoc(entityRef)
  ]);

  const eventLayoutMode = novel.storageLayout?.entities?.events?.items || 'embedded';
  const entityLayoutMode = novel.storageLayout?.entities?.[entityType]?.items || 'embedded';

  let currentEventChanges: EventEntityChange[] = [];
  let eventCharacterIds: string[] = [];
  let eventOrganizationIds: string[] = [];
  let eventLocationIds: string[] = [];

  if (eventSnap.exists()) {
    const data = eventSnap.data();
    if (data) {
      const item = (eventLayoutMode === 'embedded'
        ? (data['entityList'] as Record<string, any>)?.[eventId]
        : data) || {};
      currentEventChanges = item.eventChanges || item.details?.eventChanges || [];
      eventCharacterIds = item.characterIds || [];
      eventOrganizationIds = item.organizationIds || [];
      eventLocationIds = item.locationIds || [];
    }
  }

  let currentEntityChanges: EntityEventChange[] = [];
  if (entitySnap.exists()) {
    const data = entitySnap.data();
    if (data) {
      if (entityLayoutMode === 'embedded') {
        const entityList = data['entityList'] as Record<string, { eventChanges?: EntityEventChange[] }> | undefined;
        const item = entityList?.[entityId] || {};
        currentEntityChanges = item.eventChanges || [];
      } else {
        const item = data as { eventChanges?: EntityEventChange[] };
        currentEntityChanges = item.eventChanges || [];
      }
    }
  }

  const SUMMARY_TRACKED_FIELDS = ['name', 'type', 'shortDescription', 'mainImageUrl', 'mainImagePath', 'primaryImageId'];

  const newChange: EventEntityChange = {
    entityType,
    entityId,
    entityName,
    changes,
  };
  if (description.trim()) {
    newChange.description = description.trim();
  }

  let newEventChanges: EventEntityChange[];
  if (isEdit || currentEventChanges.some(c => c.entityId === entityId && c.entityType === entityType)) {
    newEventChanges = currentEventChanges.map(c =>
      (c.entityId === entityId && c.entityType === entityType) ? newChange : c
    );
    // Just in case it wasn't actually there despite isEdit being true (shouldn't happen, but safe fallback)
    if (!newEventChanges.some(c => c.entityId === entityId && c.entityType === entityType)) {
      newEventChanges.push(newChange);
    }
  } else {
    newEventChanges = [...currentEventChanges, newChange];
  }

  const eventPayload: {
    id: string;
    eventChanges: EventEntityChange[];
    characterIds?: string[];
    organizationIds?: string[];
    locationIds?: string[];
  } = {
    id: eventId,
    eventChanges: newEventChanges,
  };

  if (entityType === 'characters' && !eventCharacterIds.includes(entityId)) {
    eventPayload.characterIds = [...eventCharacterIds, entityId];
  } else if (entityType === 'organizations' && !eventOrganizationIds.includes(entityId)) {
    eventPayload.organizationIds = [...eventOrganizationIds, entityId];
  } else if (entityType === 'locations' && !eventLocationIds.includes(entityId)) {
    eventPayload.locationIds = [...eventLocationIds, entityId];
  }

  addEntitySaveToBatch(batch, resolver, novel, 'entities', 'events', eventPayload);

  const newEntityChange: EntityEventChange = {
    eventId,
    changes,
  };
  if (description.trim()) {
    newEntityChange.description = description.trim();
  }

  let newEntityChanges: EntityEventChange[];
  if (isEdit || currentEntityChanges.some(c => c.eventId === eventId)) {
    newEntityChanges = currentEntityChanges.map(c =>
      c.eventId === eventId ? newEntityChange : c
    );
    if (!newEntityChanges.some(c => c.eventId === eventId)) {
      newEntityChanges.push(newEntityChange);
    }
  } else {
    newEntityChanges = [...currentEntityChanges, newEntityChange];
  }

  addEntitySaveToBatch(batch, resolver, novel, 'entities', entityType, {
    id: entityId,
    eventChanges: newEntityChanges
  });

  const summaryChanges = changes.filter(c => SUMMARY_TRACKED_FIELDS.includes(c.field));
  if (summaryChanges.length > 0) {
    const summaryEventChange: Record<string, unknown> = {
      eventId,
      changes: summaryChanges,
    };
    if (description.trim()) {
      summaryEventChange.description = description.trim();
    }

    addSummaryPathUpdateToBatch(
      firestore,
      batch,
      novel,
      'entities',
      entityType,
      entityId,
      `eventChanges.${eventId}`,
      summaryEventChange
    );
  }

  if (!isReciprocal) {
    const existingPrimary = currentEventChanges.find(c => c.entityId === entityId && c.entityType === entityType);
    const oldReciprocals = existingPrimary ? generateReciprocalDeltas(entityType, entityId, existingPrimary.changes) : [];
    const newReciprocals = generateReciprocalDeltas(entityType, entityId, changes);

    const allReciprocalEntities = new Map<string, { entityType: PrimaryEntityType; entityName: string; oldChanges: FieldChange[]; newChanges: FieldChange[] }>();

    for (const r of oldReciprocals) {
      allReciprocalEntities.set(r.entityId, { entityType: r.entityType, entityName: r.entityName || r.entityId, oldChanges: r.changes, newChanges: [] });
    }
    for (const r of newReciprocals) {
      const existing = allReciprocalEntities.get(r.entityId) || { entityType: r.entityType, entityName: r.entityName || r.entityId, oldChanges: [], newChanges: [] };
      existing.newChanges = r.changes;
      allReciprocalEntities.set(r.entityId, existing);
    }

    for (const [recEntityId, data] of allReciprocalEntities.entries()) {
      const recLayoutMode = novel.storageLayout?.entities?.[data.entityType]?.items || 'embedded';
      const recRef = resolver.resolveItem(ownerId, novel.id, 'entities', data.entityType, recEntityId, novel.storageLayout).ref;
      const recSnap = await getDoc(recRef);

      if (recSnap.exists()) {
        const recData = recSnap.data();
        let recCurrentChanges: EntityEventChange[] = [];
        if (recLayoutMode === 'embedded') {
          const entityList = recData['entityList'] as Record<string, { eventChanges?: EntityEventChange[] }> | undefined;
          recCurrentChanges = entityList?.[recEntityId]?.eventChanges || [];
        } else {
          recCurrentChanges = (recData as { eventChanges?: EntityEventChange[] }).eventChanges || [];
        }

        const isMatchingReciprocal = (c: FieldChange, rc: FieldChange) => {
          if (c.field !== rc.field) return false;
          const getEntityId = (val: unknown) => {
            if (!val) return undefined;
            if (typeof val === 'string') return val;
            if (typeof val === 'object') {
              const r = val as Record<string, unknown>;
              return (r.characterId as string) || (r.id as string) || (r.organizationId as string);
            }
            return undefined;
          };
          const rcId = getEntityId(rc.newValue ?? rc.value ?? rc.identifier);
          const cId = getEntityId(c.newValue ?? c.value ?? c.identifier);
          if (rcId && cId) return rcId === cId;
          return JSON.stringify(rc.newValue ?? rc.value) === JSON.stringify(c.newValue ?? c.value);
        };

        let foundEvent = false;
        let newRecChanges = recCurrentChanges.map(ec => {
          if (ec.eventId === eventId) {
            foundEvent = true;
            const filteredChanges = ec.changes.filter(c => {
              return !data.oldChanges.some(rc => isMatchingReciprocal(c, rc));
            });
            return { ...ec, changes: [...filteredChanges, ...(data.newChanges as FieldChange[])] };
          }
          return ec;
        });

        if (!foundEvent && data.newChanges.length > 0) {
          newRecChanges.push({
            eventId,
            changes: data.newChanges as FieldChange[],
            description: description || 'Reciprocal change'
          });
        }

        newRecChanges = newRecChanges.filter(ec => ec.eventId !== eventId || ec.changes.length > 0);

        const existingRecIndexInEvent = newEventChanges.findIndex(c => c.entityId === recEntityId && c.entityType === data.entityType);
        const recEventChange = newRecChanges.find(ec => ec.eventId === eventId);

        if (recEventChange) {
          const newEvChange: EventEntityChange = {
            entityType: data.entityType,
            entityId: recEntityId,
            entityName: data.entityName,
            changes: recEventChange.changes,
            description: description || 'Reciprocal change'
          };
          if (existingRecIndexInEvent >= 0) {
            newEventChanges[existingRecIndexInEvent] = newEvChange;
          } else {
            newEventChanges.push(newEvChange);
          }
        } else {
          if (existingRecIndexInEvent >= 0) {
            newEventChanges.splice(existingRecIndexInEvent, 1);
          }
        }

        addEntitySaveToBatch(batch, resolver, novel, 'entities', data.entityType, {
          id: recEntityId,
          eventChanges: newRecChanges
        });
      }
    }

    addEntitySaveToBatch(batch, resolver, novel, 'entities', 'events', {
      id: eventId,
      eventChanges: newEventChanges
    });
  }

  await batch.commit();
}

export async function deleteEventImplication(
  firestore: Firestore,
  ownerId: string,
  novel: Novel,
  eventId: string,
  entityType: PrimaryEntityType,
  entityId: string
): Promise<void> {
  const resolver = new StorageResolver(firestore);
  const batch = writeBatch(firestore);

  const eventRef = resolver.resolveItem(ownerId, novel.id, 'entities', 'events', eventId, novel.storageLayout).ref;
  const entityRef = resolver.resolveItem(ownerId, novel.id, 'entities', entityType, entityId, novel.storageLayout).ref;

  const [eventSnap, entitySnap] = await Promise.all([
    getDoc(eventRef),
    getDoc(entityRef)
  ]);

  const eventLayoutMode = novel.storageLayout?.entities?.events?.items || 'embedded';
  const entityLayoutMode = novel.storageLayout?.entities?.[entityType]?.items || 'embedded';

  let currentEventChanges: EventEntityChange[] = [];
  if (eventSnap.exists()) {
    const data = eventSnap.data();
    if (data) {
      if (eventLayoutMode === 'embedded') {
        const entityList = data['entityList'] as Record<string, { eventChanges?: EventEntityChange[]; details?: { eventChanges?: EventEntityChange[] } }> | undefined;
        const item = entityList?.[eventId] || {};
        currentEventChanges = item.eventChanges || item.details?.eventChanges || [];
      } else {
        const item = data as { eventChanges?: EventEntityChange[]; details?: { eventChanges?: EventEntityChange[] } };
        currentEventChanges = item.eventChanges || item.details?.eventChanges || [];
      }
    }
  }

  let currentEntityChanges: EntityEventChange[] = [];
  if (entitySnap.exists()) {
    const data = entitySnap.data();
    if (data) {
      if (entityLayoutMode === 'embedded') {
        const entityList = data['entityList'] as Record<string, { eventChanges?: EntityEventChange[] }> | undefined;
        const item = entityList?.[entityId] || {};
        currentEntityChanges = item.eventChanges || [];
      } else {
        const item = data as { eventChanges?: EntityEventChange[] };
        currentEntityChanges = item.eventChanges || [];
      }
    }
  }

  let newEventChanges = currentEventChanges.filter(c =>
    !(c.entityId === entityId && c.entityType === entityType)
  );
  addEntitySaveToBatch(batch, resolver, novel, 'entities', 'events', {
    id: eventId,
    eventChanges: newEventChanges
  });

  const newEntityChanges = currentEntityChanges.filter(c =>
    c.eventId !== eventId
  );
  addEntitySaveToBatch(batch, resolver, novel, 'entities', entityType, {
    id: entityId,
    eventChanges: newEntityChanges
  });

  addSummaryPathUpdateToBatch(
    firestore,
    batch,
    novel,
    'entities',
    entityType,
    entityId,
    `eventChanges.${eventId}`,
    deleteField()
  );

  // Clean up reciprocal changes
  const implicationToDelete = currentEntityChanges.find(c => c.eventId === eventId);
  if (implicationToDelete && implicationToDelete.changes.length > 0) {
    const reciprocals = generateReciprocalDeltas(entityType, entityId, implicationToDelete.changes);

    for (const rec of reciprocals) {
      const recLayoutMode = novel.storageLayout?.entities?.[rec.entityType]?.items || 'embedded';
      const recRef = resolver.resolveItem(ownerId, novel.id, 'entities', rec.entityType, rec.entityId, novel.storageLayout).ref;

      const recSnap = await getDoc(recRef);

      if (recSnap.exists()) {
        const recData = recSnap.data();
        let recCurrentChanges: EntityEventChange[] = [];
        if (recLayoutMode === 'embedded') {
          const entityList = recData['entityList'] as Record<string, { eventChanges?: EntityEventChange[] }> | undefined;
          recCurrentChanges = entityList?.[rec.entityId]?.eventChanges || [];
        } else {
          recCurrentChanges = (recData as { eventChanges?: EntityEventChange[] }).eventChanges || [];
        }

        const isMatchingReciprocal = (c: FieldChange, rc: FieldChange) => {
          if (c.field !== rc.field) return false;
          const getEntityId = (val: unknown) => {
            if (!val) return undefined;
            if (typeof val === 'string') return val;
            if (typeof val === 'object') {
              const r = val as Record<string, unknown>;
              return (r.characterId as string) || (r.id as string) || (r.organizationId as string);
            }
            return undefined;
          };
          const rcId = getEntityId(rc.newValue ?? rc.value ?? rc.identifier);
          const cId = getEntityId(c.newValue ?? c.value ?? c.identifier);
          if (rcId && cId) return rcId === cId;
          return JSON.stringify(rc.newValue ?? rc.value) === JSON.stringify(c.newValue ?? c.value);
        };

        const newRecChanges = recCurrentChanges.map(ec => {
          if (ec.eventId === eventId) {
            const filteredChanges = ec.changes.filter(c => {
              return !rec.changes.some(rc => isMatchingReciprocal(c, rc));
            });
            return { ...ec, changes: filteredChanges };
          }
          return ec;
        }).filter(ec => ec.eventId !== eventId || ec.changes.length > 0);

        // If all changes for this reciprocal entity on this event are removed, remove from event.eventChanges too
        if (!newRecChanges.some(ec => ec.eventId === eventId)) {
          newEventChanges = newEventChanges.filter(c => !(c.entityId === rec.entityId && c.entityType === rec.entityType));
        }

        addEntitySaveToBatch(batch, resolver, novel, 'entities', rec.entityType, {
          id: rec.entityId,
          eventChanges: newRecChanges
        });
      }
    }
  }

  // Save updated eventChanges to Event document
  addEntitySaveToBatch(batch, resolver, novel, 'entities', 'events', {
    id: eventId,
    eventChanges: newEventChanges
  });

  await batch.commit();
}
